import { useParams } from "react-router-dom";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../shared/hooks/useComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import LoadingElement from "../../components/loading-element/loading-element";
import { ErrorView } from "../../components/error-view/ErrorView";
import { Manufacturer } from "../../../core/models/entities/Manufacturer";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { BoxFooter } from "../../components/BoxView/BoxFooter";
import { CheckboxOptions } from "../../components/Input/CheckboxOptions";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { useState, useEffect } from "react";
import { useApiClient } from "../../../data/clients/ApiClient";
import { useSuccessToast, useErrorToast } from "../../components/Toast/Toast";
import { useTranslation } from "react-i18next";

export function ManufacturerDetailsScreen() {
  const params = useParams();
  const id = params?.manufacturerId;
  return <ManufacturerDetailsScreenWithParam manufacturerId={+id!} />;
}

interface Props {
  manufacturerId: number;
}

export function ManufacturerDetailsScreenWithParam({ manufacturerId }: Props) {
  const { state, dataTransition } = useComponentStateMachine();
  const { manufacturerRepository } = useRepositoriesContext();

  const {
    data: manufacturer,
    isError: isManufacturerError,
    isLoading: isManufacturerLoading,
    isSuccess: isManufacturerSuccess,
  } = manufacturerRepository.useManufacturer(manufacturerId);

  const isLoading = isManufacturerLoading;
  const isSuccess = isManufacturerSuccess;
  const isError = isManufacturerError;

  dataTransition(isLoading, isSuccess, isError);

  return state === ComponentState.Succeed ? (
    <ManufacturerDetailsView manufacturer={manufacturer!} />
  ) : state === ComponentState.Loading ? (
    <div className={"w-220 h-128 relative"}>
      <LoadingElement />
    </div>
  ) : (
    <div className={"w-220 h-128 relative"}>
      <ErrorView />
    </div>
  );
}

interface ManufacturerDetailsViewProps {
  manufacturer: Manufacturer;
}

function ManufacturerDetailsView({
  manufacturer,
}: ManufacturerDetailsViewProps) {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const [isRestricted, setIsRestricted] = useState(manufacturer.isRestricted);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsRestricted(manufacturer.isRestricted);
  }, [manufacturer.isRestricted]);

  const handleIsRestrictedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsRestricted(event.target.checked);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      await apiClient.updateManufacturerRestricted(
        manufacturer.id,
        isRestricted
      );
      useSuccessToast(t("orgScreens.manufacturerRestrictionsUpdated"));
    } catch (error) {
      useErrorToast(t("orgScreens.manufacturerRestrictionsUpdateFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <JetSportBox>
      <BoxHeader2>
        <BoxHeaderTypography>
          {t("orgScreens.manufacturerHeading", {
            name: manufacturer.name,
            id: manufacturer.id,
          })}
        </BoxHeaderTypography>
      </BoxHeader2>
      <BoxContent>
        <div className="space-y-4">
          <CheckboxOptions
            name="isRestricted"
            header={t("orgScreens.restrictedAccess")}
            explain={t("orgScreens.restrictedAccessExplain")}
            checked={isRestricted}
            onChange={handleIsRestrictedChange}
            checkboxPosition="left"
          />
        </div>
      </BoxContent>
      <BoxFooter>
        <PrimaryButton onClick={handleSaveChanges} disabled={isSaving}>
          {isSaving ? t("orgScreens.saving") : t("save_changes")}
        </PrimaryButton>
      </BoxFooter>
    </JetSportBox>
  );
}
